import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  ColorPicker,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag as AntTag,
  Tooltip,
  Typography,
  Flex,
} from 'antd';
import { DeleteOutlined, EditOutlined, HeatMapOutlined, PlusOutlined, PushpinOutlined, TagsOutlined } from '@ant-design/icons';
import { ContextStatus, TagCategory, TagCategoryLabel } from '@leedpulse-monorepo/enums';
import Tag from '../../../domain/models/Tag';
import TagsContext from '../../contexts/data/TagsContext';

interface TagsListProps {
  domainId?: string | null;
  subscriptionId?: string | null;
  filterCategories?: TagCategory[];
  defaultCategory?: TagCategory;
  allEditable?: boolean;
}

interface TagFormValues {
  name: string;
  category: TagCategory;
  color: string;
  promptInstructions: string;
}

const READ_ONLY_CATEGORIES = [TagCategory.status, TagCategory.heat];
const EDITABLE_CATEGORIES = [TagCategory.typology, TagCategory.custom];

const EDITABLE_CATEGORY_ICONS: Record<string, React.ReactNode> = {
  [TagCategory.typology]: <TagsOutlined />,
  [TagCategory.custom]: <EditOutlined />,
};

const READ_ONLY_CATEGORY_ICONS: Record<string, React.ReactNode> = {
  [TagCategory.status]: <PushpinOutlined />,
  [TagCategory.heat]: <HeatMapOutlined />,
};

const readOnlyColumns = [
  {
    title: 'Couleur',
    dataIndex: 'color',
    key: 'color',
    width: 300,
    render: (_: unknown, record: Tag) => (
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: record.color,
          border: '1px solid rgba(0,0,0,0.15)',
        }}
      />
    ),
  },
  {
    title: 'Nom',
    dataIndex: 'name',
    key: 'name',
    render: (_: unknown, record: Tag) => (
      <AntTag color={record.color} style={{ color: '#FFFFFF' }}>
        {record.name}
      </AntTag>
    ),
  },
  {
    title: 'Instructions IA',
    dataIndex: 'promptInstructions',
    key: 'promptInstructions',
    ellipsis: true,
    render: (_: unknown, record: Tag) => (
      <Tooltip title={record.promptInstructions}>
        <span>{record.promptInstructions || '—'}</span>
      </Tooltip>
    ),
  },
];

const TagsList: React.FC<TagsListProps> = ({ domainId, subscriptionId, filterCategories, defaultCategory, allEditable = false }) => {
  const tagsContext = useContext(TagsContext);

  const [openModal, setOpenModal] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [form] = Form.useForm<TagFormValues>();

  useEffect(() => {
    if (
      tagsContext.status === ContextStatus.IDLE ||
      tagsContext.domainId !== (domainId ?? null) ||
      tagsContext.subscriptionId !== (subscriptionId ?? null)
    ) {
      tagsContext.fetch(domainId ?? null, subscriptionId ?? null);
    }

    return () => {
      tagsContext.reset();
    };
  }, [domainId, subscriptionId]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingTag(null);
    form.resetFields();
  };

  useEffect(() => {
    switch (tagsContext.createStatus) {
      case ContextStatus.SUCCESS:
        message.success('Tag créé avec succès');
        tagsContext.fetch(domainId ?? null, subscriptionId ?? null);
        // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
        // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
        // external system") ; le setState synchrone est le reset qui precede l'appel.
        // eslint-disable-next-line react/set-state-in-effect
        handleCloseModal();
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la création du tag');
        break;
      default:
        break;
    }
  }, [tagsContext.createStatus]);

  useEffect(() => {
    switch (tagsContext.updateStatus) {
      case ContextStatus.SUCCESS:
        message.success('Tag mis à jour avec succès');
        tagsContext.fetch(domainId ?? null, subscriptionId ?? null);
        // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
        // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
        // external system") ; le setState synchrone est le reset qui precede l'appel.
        // eslint-disable-next-line react/set-state-in-effect
        handleCloseModal();
        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la mise à jour du tag');
        break;
      default:
        break;
    }
  }, [tagsContext.updateStatus]);

  useEffect(() => {
    switch (tagsContext.deleteStatus) {
      case ContextStatus.SUCCESS:
        message.success('Tag supprimé avec succès');
        tagsContext.fetch(domainId ?? null, subscriptionId ?? null);
        break;
      case ContextStatus.ERROR:
        message.error(tagsContext.errorMessage || 'Erreur lors de la suppression du tag');
        break;
      default:
        break;
    }
  }, [tagsContext.deleteStatus]);

  const handleOpenCreate = () => {
    setEditingTag(null);
    form.resetFields();
    form.setFieldsValue({
      category: defaultCategory,
      color: '#000000',
    });
    setOpenModal(true);
  };

  const handleOpenEdit = (tag: Tag) => {
    setEditingTag(tag);
    form.setFieldsValue({
      name: tag.name,
      category: tag.category,
      color: tag.color,
      promptInstructions: tag.promptInstructions,
    });
    setOpenModal(true);
  };


  const handleSubmit = async () => {
    const values = await form.validateFields();
    const colorValue =
      typeof values.color === 'string'
        ? values.color
        : (values.color as { toHexString?: () => string })?.toHexString?.() ?? '#000000';

    if (editingTag !== null) {
      await tagsContext.submitUpdate(
        editingTag.copyWith({
          name: values.name,
          category: values.category,
          color: colorValue,
          promptInstructions: values.promptInstructions ?? '',
        })
      );
    } else {
      const newTag = new Tag(
        Tag.generateTagId(),
        values.name,
        values.category,
        domainId ?? null,
        subscriptionId ?? null,
        values.promptInstructions ?? '',

        colorValue,
        new Date(),
        new Date()
      );
      await tagsContext.submitCreate(newTag);
    }
  };

  const isSaving =
    tagsContext.createStatus === ContextStatus.LOADING ||
    tagsContext.updateStatus === ContextStatus.LOADING;

  const ALL_CATEGORIES = [...READ_ONLY_CATEGORIES, ...EDITABLE_CATEGORIES];

  const visibleReadOnlyCategories = allEditable
    ? []
    : filterCategories
    ? READ_ONLY_CATEGORIES.filter((c) => filterCategories.includes(c))
    : READ_ONLY_CATEGORIES;

  const visibleEditableCategories = allEditable
    ? (filterCategories ? ALL_CATEGORIES.filter((c) => filterCategories.includes(c)) : ALL_CATEGORIES)
    : filterCategories
    ? EDITABLE_CATEGORIES.filter((c) => filterCategories.includes(c))
    : EDITABLE_CATEGORIES;

  const CATEGORY_ICONS: Record<string, React.ReactNode> = { ...EDITABLE_CATEGORY_ICONS, ...READ_ONLY_CATEGORY_ICONS };

  const categoryOptions = visibleEditableCategories.map((cat) => ({
    value: cat,
    label: TagCategoryLabel[cat],
  }));

  const editableColumns = [
    {
      title: 'Couleur',
      dataIndex: 'color',
      key: 'color',
      width: 300,
      render: (_: unknown, record: Tag) => (
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: record.color,
            border: '1px solid rgba(0,0,0,0.15)',
          }}
        />
      ),
    },
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      render: (_: unknown, record: Tag) => (
        <AntTag color={record.color} style={{ color: '#FFFFFF' }}>
          {record.name}
        </AntTag>
      ),
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
      render: (_: unknown, record: Tag) => (
        <span>{TagCategoryLabel[record.category] ?? record.category}</span>
      ),
    },
    {
      title: 'Instructions IA',
      dataIndex: 'promptInstructions',
      key: 'promptInstructions',
      ellipsis: true,
      render: (_: unknown, record: Tag) => (
        <Tooltip title={record.promptInstructions}>
          <span>{record.promptInstructions || '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: Tag) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Modifier">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Supprimer ce tag ?"
            description="Cette action est irréversible."
            onConfirm={() => tagsContext.submitDelete(record.id)}
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Supprimer">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={tagsContext.deleteStatus === ContextStatus.LOADING}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const isLoading = tagsContext.status === ContextStatus.LOADING;

  return (
    <>
      <div className="list-container__controls" style={{ marginBottom: 16 }}>
        <h2>
          <TagsOutlined style={{ marginRight: 6 }} /> Tags
        </h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Nouveau tag
        </Button>
      </div>

      {visibleReadOnlyCategories.map((category) => {
        const categoryTags = tagsContext.tags.filter((t) => t.category === category);
        return (
          <div key={category} style={{ marginBottom: 24 }}>
            <Typography.Title level={5} style={{ marginBottom: 8 }}>
              {READ_ONLY_CATEGORY_ICONS[category]} {TagCategoryLabel[category]}
            </Typography.Title>
            <Table
              dataSource={categoryTags}
              columns={readOnlyColumns}
              rowKey="id"
              loading={isLoading}
              pagination={false}
              size="middle"
              locale={{ emptyText: 'Aucun tag' }}
            />
          </div>
        );
      })}

      <div style={{ marginTop: 8 }}>
        {visibleEditableCategories.map((category) => {
          const categoryTags = tagsContext.tags.filter((t) => t.category === category);
          return (
            <div key={category} style={{ marginBottom: 24 }}>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>
                {CATEGORY_ICONS[category]} {TagCategoryLabel[category]}
              </Typography.Title>
              <Table
                dataSource={categoryTags}
                columns={editableColumns}
                rowKey="id"
                loading={isLoading}
                pagination={false}
                size="middle"
                locale={{ emptyText: 'Aucun tag' }}
              />
            </div>
          );
        })}
      </div>

      <Modal
        title={editingTag !== null ? 'Modifier le tag' : 'Nouveau tag'}
        open={openModal}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        confirmLoading={isSaving}
        okText={editingTag !== null ? 'Enregistrer' : 'Créer'}
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Nom"
            name="name"
            rules={[{ required: true, message: 'Le nom est requis' }]}
          >
            <Input placeholder="Ex: Chaud, Acheteur, Apimo…" maxLength={50} />
          </Form.Item>

          <Form.Item
            label="Catégorie"
            name="category"
            rules={[{ required: true, message: 'La catégorie est requise' }]}
          >
            <Select options={categoryOptions} />
          </Form.Item>

          <Form.Item label="Couleur" name="color">
            <ColorPicker
              format="hex"
              showText
            />
          </Form.Item>

          <Form.Item label="Instructions IA" name="promptInstructions">
            <Input.TextArea
              rows={3}
              placeholder="Instructions supplémentaires transmises à l'IA lorsque ce tag est actif…"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TagsList;

